'use client';

import { GET_SLASH_AUTH_URL } from '@lib/config-client';
import { useClientTranslation } from '@lib/i18n/client';
import {
  getPhaserAttributesFromUrl,
  setPhaserAttributesToUrl,
} from '@modules/phaser';
import { getGetSlashAttributesFromBookingCodeSignIn } from '@modules/phaser/actions/internet-card';
import { Trans } from '@tuic/i18n';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { Button } from '@tuic/web-compositions/core/Button';
import { InputField } from '@tuic/web-compositions/core/InputField';
import { RadioButtonField } from '@tuic/web-compositions/shared/RadioButtonField';
import { useState, useEffect, useRef } from 'react';

import type { PhaserAttributes } from '@modules/phaser';
import type { Locale } from '@tuic/i18n';
import type { ChangeEventHandler, FC, FormEventHandler } from 'react';

type Values = {
  firstName: string;
  lastName: string;
  bookingCode: string;
  mpi: string;
  cabinNumber: string;
  tripCode: string;
};

type Props = {
  locale: Locale;
};

export const GetSlashAuthenticateForm: FC<Props> = ({ locale }) => {
  const { t } = useClientTranslation();
  const [authMethod, setAuthMethod] = useState<
    'bookingCode' | 'mpi' | 'myTrips'
  >('myTrips');
  const [values, setValues] = useState<Values>(() => ({
    firstName: '',
    lastName: '',
    bookingCode: '',
    mpi: '',
    cabinNumber: '',
    tripCode: '',
  }));
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const popupCheckIntervalRef = useRef<number | null>(null);

  const setFormValue: ChangeEventHandler<HTMLInputElement> = ev => {
    const value = ev.target.value;
    const name = ev.target.name;

    setValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAuthMethodChange = (value: string) => {
    setAuthMethod(value as 'bookingCode' | 'mpi' | 'myTrips');
  };

  // Function to check if popup is closed and reset pending state
  const checkPopupStatus = () => {
    if (popupRef.current?.closed) {
      // Popup was manually closed
      setPending(false);

      // Clear the interval
      if (popupCheckIntervalRef.current !== null) {
        window.clearInterval(popupCheckIntervalRef.current);
        popupCheckIntervalRef.current = null;
      }

      // Clear the popup reference
      popupRef.current = null;
    }
  };

  const handleSubmit: FormEventHandler = async event => {
    event.preventDefault();

    setPending(true);
    setError(undefined);

    if (authMethod === 'mpi') {
      const shipCode = values.tripCode.trim().substring(0, 3) ?? '';

      const mpi = parseInt(values.mpi ?? '0', 10);
      assignAttributes({
        shipCode,
        mpi: mpi > 0 ? mpi : undefined,
        cabinNumber: values.cabinNumber,
      });

      return;
    }

    if (authMethod === 'bookingCode') {
      const attributes = await getGetSlashAttributesFromBookingCodeSignIn({
        locale,
        ...values,
      });

      if (typeof attributes === 'string') {
        setError(attributes);
        setPending(false);

        return;
      }

      const mpi = parseInt(attributes.mpi ?? '0', 10);
      assignAttributes({
        ...attributes,
        mpi: mpi > 0 ? mpi : undefined,
      });

      return;
    }

    // Clear any existing interval
    if (popupCheckIntervalRef.current !== null) {
      window.clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }

    // Open a popup to the constructed URL
    const popup = openPopUp(GET_SLASH_AUTH_URL);

    if (!popup) {
      setError(
        t('Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.'),
      );
      setPending(false);
      return;
    }

    // Store the popup reference
    popupRef.current = popup;

    // Set up an interval to check if the popup is closed
    popupCheckIntervalRef.current = window.setInterval(checkPopupStatus, 500);
  };

  // Set up event listener for postMessage from the popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check if the message is from our popup
      if (!event.data || event.data.type !== 'PHASER_GET_SLASH_AUTH') {
        return;
      }

      const response = event.data.response;

      // Check if we have an error (response is a string)
      if (typeof response === 'string') {
        setError(response);
        setPending(false);
        return;
      }

      // Call assignAttributes with the mpi parameter
      assignAttributes(response);
    };

    // Add event listener
    window.addEventListener('message', handleMessage);

    // Clean up
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [t]);

  // Clean up interval when component unmounts
  useEffect(() => {
    return () => {
      const attrs = window
        ? getPhaserAttributesFromUrl(window.location.href)
        : {};

      setValues(prev => ({
        ...prev,
        mpi: attrs?.mpi?.toString() ?? '',
        cabinNumber: attrs?.cabinNumber ?? '',
        tripCode: attrs?.shipCode ?? '',
      }));

      if (attrs.mpi || attrs.cabinNumber || attrs.shipCode) {
        setAuthMethod('mpi');
      }

      if (popupCheckIntervalRef.current !== null) {
        window.clearInterval(popupCheckIntervalRef.current);
        popupCheckIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <RadioButtonField
            name="authMethod"
            value="myTrips"
            checked={authMethod === 'myTrips'}
            onChange={() => handleAuthMethodChange('myTrips')}
            label={t('Ich möchte mich über Meine Reisen anmelden')}
            note={
              <Trans>
                Mit dieser Methode können Sie direkt aus den Reisen auf{' '}
                <a
                  className="underline hover:no-underline"
                  target="_blank"
                  href="https://meinereisen.meinschiff.com"
                  rel="noreferrer"
                >
                  meinereisen.meinschiff.com
                </a>{' '}
                auswählen.
              </Trans>
            }
          />
          <RadioButtonField
            name="authMethod"
            value="bookingCode"
            checked={authMethod === 'bookingCode'}
            onChange={() => handleAuthMethodChange('bookingCode')}
            label={t('Ich möchte mich mit Name & Vorgangsnummer anmelden')}
          />
          <RadioButtonField
            name="authMethod"
            value="mpi"
            checked={authMethod === 'mpi'}
            onChange={() => handleAuthMethodChange('mpi')}
            label={t(
              'Ich möchte mich mit MPI, Kabinennummer und Trip- oder Schiffs-Code anmelden',
            )}
          />
        </div>

        {authMethod === 'bookingCode' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField
              label={t('Vorname')}
              name="firstName"
              placeholder={t('Vorname')}
              onChange={setFormValue}
              value={values.firstName}
            />
            <InputField
              label={t('Nachname')}
              name="lastName"
              placeholder={t('Nachname')}
              onChange={setFormValue}
              value={values.lastName}
            />
            <InputField
              label={t('Vorgangsnummer')}
              name="bookingCode"
              placeholder={t('Vorgangsnummer')}
              onChange={setFormValue}
              value={values.bookingCode}
            />
          </div>
        )}

        {authMethod === 'mpi' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField
              label={t('MPI')}
              name="mpi"
              placeholder={t('MPI')}
              onChange={setFormValue}
              value={values.mpi}
            />
            <InputField
              label={t('Kabinennummer')}
              name="cabinNumber"
              placeholder={t('Kabinennummer')}
              onChange={setFormValue}
              value={values.cabinNumber}
            />
            <InputField
              label={t('Trip- oder Schiffs-Code')}
              name="tripCode"
              placeholder={t('Trip- oder Schiffs-Code')}
              onChange={setFormValue}
              value={values.tripCode}
            />
          </div>
        )}

        <Button variant="secondary" type="submit" indicating={pending}>
          {t('Anmelden')}
        </Button>

        {error && <Alert mode="error">{error}</Alert>}
      </form>
    </div>
  );
};

const assignAttributes = (attrs: PhaserAttributes) => {
  const currentAttributes = getPhaserAttributesFromUrl(window.location.href);

  const url = setPhaserAttributesToUrl(window.location.href, {
    ...currentAttributes,
    mockInternetCard: false,
    ...attrs,
  });

  window.location.href = url.toString();
};

const openPopUp = (url: string) => {
  const width = 500;
  const height = 1100;
  // Calculate the position to center the popup
  const left = (window.screen.width - width) / 2;
  const top = (window.screen.height - height) / 2;

  // Open the popup with specified dimensions and position
  const popup = window.open(
    url,
    'getSlashAuth',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`,
  );

  // Focus the popup
  if (popup) {
    popup.focus();
  }

  return popup;
};
